/**
 * ChatWindow - Main chat conversation window
 * Displays messages and handles sending
 */
import React, { useState, useEffect, useRef } from "react";
import MessageInput from "./MessageInput";
import { chatAPI } from "../../utils/chatAPI";
import { useWebSocket } from "../../utils/useWebSocket";
import { useAuth } from "../../context/AuthContext";
import { showToast } from "../Toast";
import "./ChatWindow.css";

const ChatWindow = ({ user, organizationId, onNewMessage }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;

  // WebSocket connection (simulated for now)
  const { sendMessage: sendWsMessage, isConnected } = useWebSocket(
    organizationId,
    handleWebSocketMessage,
  );

  // Fetch message history on mount or user change
  useEffect(() => {
    if (currentUserId) {
      fetchMessages();
    }
  }, [user.id, organizationId, currentUserId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Automatically mark messages as read when conversation is viewed
  useEffect(() => {
    if (!currentUserId || !organizationId || !user?.id || messages.length === 0) {
      return;
    }

    // Messages that are addressed to the current user and not yet read
    const unreadReceived = messages.filter(
      (msg) => msg.receiver_id === currentUserId && !msg.read,
    );

    if (unreadReceived.length === 0) {
      return;
    }

    // Optimistically update UI
    setMessages((prev) =>
      prev.map((msg) =>
        msg.receiver_id === currentUserId && !msg.read
          ? { ...msg, read: true }
          : msg,
      ),
    );

    // Notify backend (fire and forget)
    Promise.all(
      unreadReceived.map((msg) =>
        chatAPI.markAsRead(organizationId, msg.id).catch(() => null),
      ),
    );
  }, [messages, currentUserId, organizationId, user?.id]);

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await chatAPI.getMessages(organizationId, user.id);
      setMessages(data);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (content) => {
    try {
      if (!currentUserId) {
        throw new Error("Not authenticated");
      }
      // Optimistically add message to UI
      const tempMessage = {
        id: `temp-${Date.now()}`,
        sender_id: currentUserId,
        receiver_id: user.id,
        content,
        timestamp: new Date().toISOString(),
        read: false,
      };
      setMessages((prev) => [...prev, tempMessage]);

      // Send via API
      const newMessage = await chatAPI.sendMessage(organizationId, {
        receiver: user.id,
        content,
      });

      // Replace temp message with real one
      setMessages((prev) =>
        prev.map((msg) => (msg.id === tempMessage.id ? newMessage : msg)),
      );

      // Send via WebSocket for real-time delivery
      if (isConnected) {
        sendWsMessage({
          type: "chat_message",
          receiver_id: user.id,
          content,
        });
      }

      onNewMessage?.();
    } catch (err) {
      console.error("Error sending message:", err);
      // Remove temp message on error
      setMessages((prev) =>
        prev.filter((msg) => !msg.id.toString().startsWith("temp-")),
      );
      showToast.error("Failed to send message");
    }
  };

  function handleWebSocketMessage(wsMessage) {
    if (wsMessage.type === "chat_message") {
      const msg = wsMessage.message;
      // Only add if it's part of this conversation
      if (
        (msg.sender_id === user.id && msg.receiver_id === currentUserId) ||
        (msg.sender_id === currentUserId && msg.receiver_id === user.id)
      ) {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
    } else if (wsMessage.type === "message_read") {
      // Update read status
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === wsMessage.message_id ? { ...msg, read: true } : msg,
        ),
      );
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString();
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <div className="chat-window">
      {/* Chat Header */}
      <div className="chat-window-header">
        <div className="chat-user-info">
          <div className="chat-user-avatar">
            <div className="avatar-circle">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <span
              className={`user-status-indicator ${user.is_online ? "online" : "offline"}`}
            />
          </div>
          <div className="chat-user-details">
            <h3>{user.name}</h3>
            <span
              className={`user-status-text ${user.is_online ? "online" : "offline"}`}
            >
              {user.is_online ? "Online" : "Offline"}
            </span>
          </div>
        </div>

        <div className="chat-actions">
          {/* WebSocket status indicator */}
          <span
            className={`ws-indicator ${isConnected ? "connected" : "disconnected"}`}
            title={isConnected ? "Connected" : "Disconnected"}
          >
            ⬤
          </span>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="chat-messages">
        {loading ? (
          <div className="chat-loading">
            <p>Loading messages...</p>
          </div>
        ) : error ? (
          <div className="chat-error">
            <p>{error}</p>
            <button onClick={fetchMessages}>Retry</button>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date Divider */}
              <div className="message-date-divider">
                <span>{date}</span>
              </div>

              {/* Messages */}
              {dateMessages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${
                    message.sender_id === currentUserId
                      ? "message-sent"
                      : "message-received"
                  }`}
                >
                  <div className="message-bubble">
                    <p className="message-content">{message.content}</p>
                    <div className="message-meta">
                      <span className="message-time">
                        {formatTime(message.timestamp)}
                      </span>
                      {message.sender_id === currentUserId && (
                        <span
                          className={`message-status ${message.read ? "read" : "sent"}`}
                        >
                          {message.read ? "✓✓" : "✓"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <MessageInput
        onSendMessage={handleSendMessage}
        disabled={loading || !!error}
      />
    </div>
  );
};

export default ChatWindow;
